import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCalendarAlt, 
  FaBook, 
  FaClock, 
  FaChartBar, 
  FaLightbulb, 
  FaBrain, 
  FaHourglassHalf,
  FaPlus,
  FaEdit,
  FaTrash,
  FaPlay,
  FaCheckCircle,
  FaCheck,
  FaTimes
} from 'react-icons/fa';
import './ExamPreparation.css';

const ExamPreparation = () => {
  const [studyPlan, setStudyPlan] = useState({
    startDate: '',
    examDate: '',
    subjects: [],
    dailyStudyHours: 4,
    weeklyGoals: [],
    progress: {}
  });

  const [activeTab, setActiveTab] = useState('overview');
  const [selectedExam, setSelectedExam] = useState(null);
  const [examResults, setExamResults] = useState({});
  const [currentAnswers, setCurrentAnswers] = useState({});

  // Sample subjects for demonstration
  const sampleSubjects = [
    { id: 1, name: 'Mathematics', topics: ['Algebra', 'Calculus', 'Statistics'] },
    { id: 2, name: 'Physics', topics: ['Mechanics', 'Electromagnetism', 'Thermodynamics'] },
    { id: 3, name: 'Chemistry', topics: ['Organic', 'Inorganic', 'Physical Chemistry'] }
  ];

  // Sample mock exams
  const mockExams = [
    {
      id: 1,
      title: 'Mathematics Practice Test 1',
      subject: 'Mathematics',
      duration: 120, // minutes
      totalMarks: 100,
      questions: [
        {
          id: 1,
          question: 'Solve the quadratic equation: x² + 5x + 6 = 0',
          options: ['x = -2, -3', 'x = 2, 3', 'x = -1, -4', 'x = 1, 4'],
          correctAnswer: 'x = -2, -3'
        },
        {
          id: 2,
          question: 'What is the derivative of sin(x)?',
          options: ['cos(x)', '-sin(x)', '-cos(x)', 'tan(x)'],
          correctAnswer: 'cos(x)'
        }
      ]
    },
    {
      id: 2,
      title: 'Physics Practice Test 1',
      subject: 'Physics',
      duration: 90,
      totalMarks: 100,
      questions: [
        {
          id: 1,
          question: 'What is the SI unit of force?',
          options: ['Newton', 'Joule', 'Pascal', 'Watt'],
          correctAnswer: 'Newton'
        },
        {
          id: 2,
          question: 'What is the formula for kinetic energy?',
          options: ['KE = 1/2 mv²', 'KE = mgh', 'KE = mv', 'KE = 1/2 mgh'],
          correctAnswer: 'KE = 1/2 mv²'
        }
      ]
    }
  ];

  const handleStartExam = (exam) => {
    setSelectedExam(exam);
    setCurrentAnswers({});
  };

  const handleAnswerSubmit = (questionId, answer) => {
    setCurrentAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleExamSubmit = () => {
    if (!selectedExam) return;

    let score = 0;
    selectedExam.questions.forEach(question => {
      if (currentAnswers[question.id] === question.correctAnswer) {
        score += selectedExam.totalMarks / selectedExam.questions.length;
      }
    });

    const result = {
      examId: selectedExam.id,
      score,
      totalMarks: selectedExam.totalMarks,
      date: new Date().toISOString(),
      answers: currentAnswers
    };

    setExamResults(prev => ({
      ...prev,
      [selectedExam.id]: result
    }));

    setSelectedExam(null);
    setCurrentAnswers({});
  };

  const generateStudyPlan = () => {
    if (!studyPlan.startDate || !studyPlan.examDate) return;

    const start = new Date(studyPlan.startDate);
    const end = new Date(studyPlan.examDate);
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const totalStudyHours = totalDays * studyPlan.dailyStudyHours;

    // Calculate hours per subject based on difficulty and importance
    const subjectHours = {};
    studyPlan.subjects.forEach(subject => {
      subjectHours[subject.id] = Math.floor(totalStudyHours / studyPlan.subjects.length);
    });

    // Generate weekly goals
    const weeklyGoals = [];
    let currentWeek = 1;
    let remainingDays = totalDays;

    while (remainingDays > 0) {
      const weekDays = Math.min(7, remainingDays);
      const weekHours = weekDays * studyPlan.dailyStudyHours;

      weeklyGoals.push({
        week: currentWeek,
        hours: weekHours,
        subjects: studyPlan.subjects.map(subject => ({
          id: subject.id,
          hours: Math.floor(weekHours / studyPlan.subjects.length),
          topics: subject.topics.slice(0, 2) // Focus on 2 topics per week
        }))
      });

      currentWeek++;
      remainingDays -= weekDays;
    }

    setStudyPlan(prev => ({
      ...prev,
      weeklyGoals
    }));
  };

  const renderOverview = () => (
    <div className="overview-section">
      <h2>Exam Preparation Overview</h2>
      <div className="overview-grid">
        <div className="overview-card">
          <FaCalendarAlt className="icon" />
          <h3>Timeline</h3>
          <p>Start Date: {studyPlan.startDate || 'Not set'}</p>
          <p>Exam Date: {studyPlan.examDate || 'Not set'}</p>
          <p>Total Study Hours: {studyPlan.weeklyGoals.reduce((acc, week) => acc + week.hours, 0)}</p>
        </div>
        <div className="overview-card">
          <FaBook className="icon" />
          <h3>Subjects</h3>
          <ul>
            {studyPlan.subjects.map(subject => (
              <li key={subject.id}>{subject.name}</li>
            ))}
          </ul>
        </div>
        <div className="overview-card">
          <FaChartBar className="icon" />
          <h3>Progress</h3>
          <div className="progress-stats">
            <p>Completed Topics: {Object.keys(studyPlan.progress).length}</p>
            <p>Remaining Topics: {studyPlan.subjects.reduce((acc, subject) => acc + subject.topics.length, 0) - Object.keys(studyPlan.progress).length}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStudyPlan = () => (
    <div className="study-plan-section">
      <h2>Weekly Study Plan</h2>
      <div className="study-plan-grid">
        {studyPlan.weeklyGoals.map(week => (
          <div key={week.week} className="week-card">
            <h3>Week {week.week}</h3>
            <div className="week-details">
              <p><FaClock /> {week.hours} hours</p>
              <div className="subjects-list">
                {week.subjects.map(subject => (
                  <div key={subject.id} className="subject-item">
                    <h4>{sampleSubjects.find(s => s.id === subject.id)?.name}</h4>
                    <p>{subject.hours} hours</p>
                    <ul>
                      {subject.topics.map((topic, index) => (
                        <li key={index}>{topic}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMockExams = () => (
    <div className="mock-exams-section">
      <h2>Mock Exams</h2>
      <div className="mock-exams-grid">
        {mockExams.map(exam => (
          <div key={exam.id} className="exam-card">
            <div className="exam-header">
              <h3>{exam.title}</h3>
              <span className="subject-tag">{exam.subject}</span>
            </div>
            <div className="exam-details">
              <p><FaClock /> Duration: {exam.duration} minutes</p>
              <p><FaBook /> Questions: {exam.questions.length}</p>
              <p><FaChartBar /> Total Marks: {exam.totalMarks}</p>
            </div>
            {examResults[exam.id] ? (
              <div className="exam-result">
                <h4>Previous Result</h4>
                <p>Score: {examResults[exam.id].score}/{exam.totalMarks}</p>
                <p>Date: {new Date(examResults[exam.id].date).toLocaleDateString()}</p>
              </div>
            ) : null}
            <button 
              className="start-exam-btn"
              onClick={() => handleStartExam(exam)}
            >
              {examResults[exam.id] ? 'Retake Exam' : 'Start Exam'}
            </button>
          </div>
        ))}
      </div>

      {selectedExam && (
        <div className="exam-modal">
          <div className="exam-content">
            <div className="exam-header">
              <h2>{selectedExam.title}</h2>
              <button className="close-btn" onClick={() => setSelectedExam(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="questions-container">
              {selectedExam.questions.map((question, index) => (
                <div key={question.id} className="question-card">
                  <h3>Question {index + 1}</h3>
                  <p>{question.question}</p>
                  <div className="options-grid">
                    {question.options.map((option, optIndex) => (
                      <label key={optIndex} className="option-label">
                        <input
                          type="radio"
                          name={`question-${question.id}`}
                          value={option}
                          checked={currentAnswers[question.id] === option}
                          onChange={() => handleAnswerSubmit(question.id, option)}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button 
              className="submit-exam-btn"
              onClick={handleExamSubmit}
              disabled={Object.keys(currentAnswers).length !== selectedExam.questions.length}
            >
              Submit Exam
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderTips = () => (
    <div className="tips-section">
      <h2>Study Tips & Strategies</h2>
      <div className="tips-grid">
        <div className="tip-card">
          <FaLightbulb className="icon" />
          <h3>Active Learning</h3>
          <ul>
            <li>Practice with past papers</li>
            <li>Create mind maps</li>
            <li>Teach concepts to others</li>
            <li>Use flashcards for memorization</li>
          </ul>
        </div>
        <div className="tip-card">
          <FaBrain className="icon" />
          <h3>Memory Techniques</h3>
          <ul>
            <li>Spaced repetition</li>
            <li>Mnemonics for complex concepts</li>
            <li>Visual learning aids</li>
            <li>Regular review sessions</li>
          </ul>
        </div>
        <div className="tip-card">
          <FaHourglassHalf className="icon" />
          <h3>Time Management</h3>
          <ul>
            <li>Follow the study schedule</li>
            <li>Take regular breaks</li>
            <li>Prioritize difficult topics</li>
            <li>Review progress weekly</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="exam-preparation">
      <div className="exam-header">
        <h1>National Exam Preparation</h1>
        <p>Create and follow a structured study plan for your upcoming exam</p>
      </div>

      <div className="exam-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'plan' ? 'active' : ''}`}
          onClick={() => setActiveTab('plan')}
        >
          Study Plan
        </button>
        <button 
          className={`tab ${activeTab === 'mock-exams' ? 'active' : ''}`}
          onClick={() => setActiveTab('mock-exams')}
        >
          Mock Exams
        </button>
        <button 
          className={`tab ${activeTab === 'tips' ? 'active' : ''}`}
          onClick={() => setActiveTab('tips')}
        >
          Tips & Strategies
        </button>
      </div>

      <div className="exam-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'plan' && renderStudyPlan()}
        {activeTab === 'mock-exams' && renderMockExams()}
        {activeTab === 'tips' && renderTips()}
      </div>
    </div>
  );
};

export default ExamPreparation; 